// SPDX-License-Identifier: GPL-3.0-only
// Source-derived subset of OpenSaga GizmoPickups_Reset/CollideList/Draw/Update,
// GizmoPickup_Activate/TurnOnPickup and GizmoPickups_SetOnOff. Authored fixed
// pickups only: temporary flying coins, GIT scripts, super-counters, particles,
// room/visibility queries and save/economy effects belong to their callers.
import { PICKUP_TYPES } from './gizmos.mjs';
import { sphereOverlapScaleY } from './combat.mjs';

const METRICS = Object.freeze({
  s: [0.045, 0.045, 10], g: [0.045, 0.045, 10], b: [0.045, 0.045, 6], p: [0.045, 0.045, 6],
  m: [0.125, 0.15, 2], h: [0.1, 0.08, 4], r: [0.12, 0.06, 2], c: [0.125, 0.15, 2], t: [0.1, 0.1, 5], u: [0.125, 0.125, 2],
});
const TAU = Math.PI * 2;
function positive(n, name) { if (!Number.isFinite(n) || n <= 0) throw new Error(`Invalid ${name}.`); return n; }
function vector(p, name) { if (!p || !['x', 'y', 'z'].every(k => Number.isFinite(p[k]))) throw new Error(`Invalid ${name}.`); return p; }
function ids(value, name) { if (value === undefined) return new Set(); if (!(value instanceof Set) && !Array.isArray(value)) throw new Error(`Invalid ${name}.`); if (![...value].every(v => typeof v === 'string')) throw new Error(`Invalid ${name}.`); return new Set(value); }

export function canCollect(pickup) {
  return !!(pickup.active && pickup.enabled && !pickup.collected && pickup.typeEnabled &&
    (pickup.drawn || (pickup.configFlags & 4)));
}

export function pickupCollision(pickup, actor, options = {}) {
  if (!actor || actor.collectDisabled || !canCollect(pickup)) return false;
  // The original config bit 0x04 bypasses distance and DRAWN tests entirely.
  if (pickup.configFlags & 4) return true;
  const metrics = METRICS[pickup.effectiveTypeCode]; if (!metrics) return false;
  const position = vector(pickup.collisionPosition, 'pickup position');
  const center = vector(actor.collisionPosition, 'actor collision position'), min = vector(actor.collisionMin, 'actor collision minimum'), max = vector(actor.collisionMax, 'actor collision maximum');
  if (['x', 'y', 'z'].some(k => min[k] > max[k])) throw new Error('Invalid actor collision bounds.');
  const normalScale = positive(options.pickupScale ?? pickup.pickupScale, 'pickup scale'), magnet = !!actor.coinMagnet;
  const collideScale = actor.collideScale ?? 0; if (!Number.isFinite(collideScale) || collideScale < 0) throw new Error('Invalid pickup collision scale.');
  const planar = !!actor.collide2D || collideScale !== 0;
  let scaled = normalScale * (magnet ? 3 : 1);
  if (collideScale !== 0) scaled *= collideScale;
  if (planar && actor.vehicleArea) scaled *= 2;
  const scale = pickup.score === 0 ? normalScale : scaled;
  const radiusX = metrics[0] * metrics[2] * scale;
  const radiusY = scale * metrics[1] * (magnet ? metrics[2] : 1);
  if (min.x > position.x + radiusX || position.x - radiusX > max.x || min.z > position.z + radiusX || position.z - radiusX > max.z) return false;
  const planarOverlap = () => {
    const radius = radiusX + positive(actor.planarRadius ?? actor.collisionRadius, 'actor planar radius');
    return (position.x - center.x) ** 2 + (position.z - center.z) ** 2 < radius ** 2;
  };
  if (planar) return planarOverlap();
  if (min.y > position.y + radiusY || position.y - radiusY > max.y) return false;
  if (actor.mounted) return planarOverlap();
  return sphereOverlapScaleY(position, radiusX, radiusY, center,
    positive(actor.collisionRadius, 'actor collision radius'), positive(actor.yRadius, 'actor vertical collision radius'));
}

export function createPickupState(gizmos, options = {}) {
  const mode = options.mode ?? 'story'; if (!['story', 'freeplay', 'challenge'].includes(mode)) throw new Error('Unsupported pickup game mode.');
  for (const key of ['areaFlags', 'arcadeFlags']) if (options[key] !== undefined && (!Number.isInteger(options[key]) || options[key] < 0 || options[key] > 0xffffffff)) throw new Error(`Invalid ${key}.`);
  if (options.superStory !== undefined && typeof options.superStory !== 'boolean') throw new Error('Invalid super-story state.');
  const progress = options.progress ?? {}, collected = ids(progress.collectedIds, 'collected pickup IDs'), activated = ids(progress.activatedIds, 'activated pickup IDs');
  const hidden = ids(progress.hiddenIds, 'hidden pickup IDs'), disabled = ids(progress.disabledIds, 'disabled pickup IDs');
  const minikits = new Set([...ids(progress.minikitNames, 'saved minikit names')].map(n => n.toLowerCase()));
  const pickupScale = positive(options.pickupScale ?? gizmos.pickupSettings?.pickupScale ?? 1, 'pickup scale');
  const random = options.random16 ?? (() => 0); if (typeof random !== 'function') throw new Error('Invalid pickup random source.');
  function random16() { const value = random(); if (!Number.isInteger(value) || value < 0 || value > 65535) throw new Error('Pickup random source must return an unsigned 16-bit integer.'); return value; }
  const events = [], byId = new Map();
  const typeEnabled = code => {
    if (mode === 'challenge') return code === 'c';
    if (code === 'c') return false;
    if (code === 'r' && progress.redBrickCollected) return false;
    if (code === 'm' && options.superStory) return false;
    if (code === 'u' && ((options.areaFlags ?? 0) & 0x100)) return false;
    if (((options.arcadeFlags ?? 0) & 0x20) && code !== 'u' && code !== 'h') return false;
    return !!PICKUP_TYPES[code];
  };
  const pickups = gizmos.pickups.map(authored => {
    if (byId.has(authored.id)) throw new Error('Duplicate pickup ID.');
    if (!Array.isArray(authored.position) || authored.position.length !== 3 || !authored.position.every(Number.isFinite)) throw new Error('Invalid authored pickup position.');
    const alternate = authored.typeCode === 'm' && (options.superStory || minikits.has(authored.name.toLowerCase()));
    const effectiveTypeCode = alternate ? 'b' : authored.typeCode;
    const type = PICKUP_TYPES[effectiveTypeCode] ?? { type: 'unknown', score: 0, modelName: null, modelVariants: 0 };
    const authoredVariants = PICKUP_TYPES[authored.typeCode]?.modelVariants ?? 0;
    const pickup = { ...authored, position: authored.position.slice(), collisionPosition: { x: authored.position[0], y: authored.position[1], z: authored.position[2] },
      effectiveTypeCode, collectibleType: type.type, score: type.score, modelName: type.modelName, pickupScale,
      modelVariant: authoredVariants > 1 ? Math.floor(random16() / (Math.floor(65535 / authoredVariants) + 1)) : 0,
      drawRotation: random16(), active: true, enabled: !disabled.has(authored.id), visible: !hidden.has(authored.id),
      collected: collected.has(authored.id), drawn: false, drawVisible: true, activated: activated.has(authored.id),
      alternateType: alternate, typeEnabled: typeEnabled(effectiveTypeCode), runtimeState: 'fresh-reset-subset' };
    if ((pickup.configFlags & 1) && !pickup.activated) pickup.enabled = pickup.visible = false;
    const flags = options.gizmoFlags instanceof Map ? options.gizmoFlags.get(pickup.id) : Object.hasOwn(options.gizmoFlags ?? {}, pickup.id) ? options.gizmoFlags[pickup.id] : undefined;
    if (flags !== undefined) {
      if (Number.isInteger(flags) && flags >= 0 && flags <= 255) {
        ['active', 'enabled', 'visible', 'collected', 'drawn', 'drawVisible', 'alternateType', 'activated'].forEach((key, bit) => { pickup[key] = !!(flags & (1 << bit)); });
      } else if (flags && typeof flags === 'object') {
        for (const key of ['active', 'enabled', 'visible', 'collected', 'drawn', 'drawVisible', 'activated']) {
          if (Object.hasOwn(flags, key)) { if (typeof flags[key] !== 'boolean') throw new Error('Invalid pickup gizmo state.'); pickup[key] = flags[key]; }
        }
      } else throw new Error('Invalid pickup gizmo flags.');
    }
    const resolvedCode = pickup.alternateType ? 'b' : pickup.typeCode, resolved = PICKUP_TYPES[resolvedCode];
    pickup.effectiveTypeCode = resolvedCode; pickup.collectibleType = resolved?.type ?? 'unknown'; pickup.score = resolved?.score ?? 0;
    pickup.modelName = resolved?.modelName ?? null; pickup.typeEnabled = typeEnabled(resolvedCode);
    if ((pickup.configFlags & 1) && !pickup.activated) pickup.enabled = pickup.visible = false;
    if (collected.has(pickup.id)) pickup.collected = true;
    byId.set(pickup.id, pickup); return pickup;
  });
  function get(id) { const p = byId.get(id); if (!p) throw new Error(`Unknown pickup ID: ${id}`); return p; }
  return {
    mode, pickupScale, pickups, byId, events,
    update(dt) {
      if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Pickup update step must be between zero and one second.');
      for (const p of pickups) if (p.active) { p.drawVisible = true; p.drawRotation = Math.floor(p.drawRotation + 16384 * dt) & 65535; }
    },
    // Original GizmoPickup_Activate: group-zero activation changes ENABLED,
    // not VISIBILITY. Script TurnOnPickup is exposed separately below.
    activate(id, on = true) {
      if (typeof on !== 'boolean') throw new Error('Invalid pickup activation.');
      const p = get(id); p.enabled = on; if (on) p.activated = true;
      if (p.activationGroup) for (const other of pickups) if (other.activationGroup === p.activationGroup) other.enabled = other.visible = on;
      const event = { type: 'activated', id, enabled: on, group: p.activationGroup }; events.push(event); return event;
    },
    turnOn(id) { const p = get(id); p.enabled = p.visible = p.activated = true; const e = { type: 'turned_on', id }; events.push(e); return e; },
    setVisibility(id, visible) { if (typeof visible !== 'boolean') throw new Error('Invalid pickup visibility.'); get(id).visible = visible; },
    markDrawn(id, drawn) { if (typeof drawn !== 'boolean') throw new Error('Invalid pickup draw state.'); get(id).drawn = drawn; },
    getDrawState(id) {
      const p = get(id), rotationY = p.drawRotation * TAU / 65536;
      const tumble = ['m', 'r', 'c', 'u'].includes(p.effectiveTypeCode), rotate = tumble || ['h', 't'].includes(p.effectiveTypeCode);
      return { visible: p.active && p.visible && !p.collected && p.drawVisible && p.typeEnabled, position: p.position.slice(),
        rotationY: rotate ? rotationY : 0, rotationX: tumble ? Math.trunc(Math.sin((p.drawRotation & 0x7fff) * TAU / 65536) * 1820) * TAU / 65536 : 0,
        scale: pickupScale, bob: 0, modelName: p.modelName, modelVariant: p.modelVariant, effectiveTypeCode: p.effectiveTypeCode };
    },
    collect(id, actor) {
      const p = get(id); if (!pickupCollision(p, actor)) return null;
      p.collected = true;
      const event = { type: 'collected', id: p.id, name: p.name, originalTypeCode: p.typeCode, effectiveTypeCode: p.effectiveTypeCode,
        collectibleType: p.collectibleType, score: p.score, position: p.position.slice() };
      events.push(event); return event;
    },
    drainEvents() { return events.splice(0); },
  };
}
